import React, { useEffect, useState } from 'react';
import {
    Breadcrumbs,
    Typography,
    Box,
    CircularProgress,
    Tabs,
    Tab,
} from '@mui/material';

import {
    asyncSharepointGetSites,
    asyncSharepointGetDrives,
    asyncSharepointGetFiles,
    asyncSharepointGetMyDriveRoot,
    asyncSharepointGetDriveItemChildren,
    asyncRevokeToken,
} from '../../../../services/sharepoint-graph.service';

import ButtonGeneral from '../../general/buttons/buttonGeneral';
import { Dialog } from '../../general/dialog/dialog';
import {
    NAV_TYPES,
    NavEntry,
    SharepointItem,
} from '../../../../shared/types/sharepoint';
import { FileList } from './FileList';
import { ToastSwal } from '../../general/toastSwals/toastSwal';

interface FileExplorerProps {
    open: boolean;
    onClose: () => void;
    selectedItems: SharepointItem[];
    setSharepointItems: React.Dispatch<React.SetStateAction<SharepointItem[]>>;
    setSharepointModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setIsSharepointAuth: React.Dispatch<React.SetStateAction<boolean>>;
}

export const FileExplorer = ({
    open,
    onClose,
    selectedItems,
    setSharepointItems,
    setSharepointModalOpen,
    setIsSharepointAuth,
}: FileExplorerProps) => {
    const [loading, setLoading] = useState(false);
    const [navStack, setNavStack] = useState<NavEntry[]>([
        { type: NAV_TYPES.ROOT, name: 'Inicio' },
    ]);
    const [myDriveItems, setMyDriveItems] = useState<SharepointItem[]>([]);
    const [sitesItems, setSitesItems] = useState<SharepointItem[]>([]);
    const [currentItems, setCurrentItems] = useState<SharepointItem[]>([]);
    const [tabIndex, setTabIndex] = useState(0);

    const atRoot = navStack.length === 1;

    const onFilesSelected = (items: SharepointItem[]) => {
        setSharepointItems(items);
    };
    const onRevokeAccess = async () => {
        setSharepointItems([]);
        setSharepointModalOpen(false);
        ToastSwal.fire({
            text: 'Permiso revocado con éxito',
            icon: 'success',
            timer: 20000,
            position: 'center-end',
        });
        try {
            await asyncRevokeToken();
            setIsSharepointAuth(false);
        } catch (error) {
            ToastSwal.fire({
                text: 'Ocurrió un error revocando el acceso, intenta nuevamente más tarde',
                icon: 'error',
                timer: 20000,
                position: 'center-end',
            });
        }
    };

    const onClearSelection = () => {
        setSharepointItems([]);
    };
    useEffect(() => {
        const handler = (event: any) => {
            if (event.origin !== process.env.REACT_APP_FRONT_BASE_URL) return;
            if (event.data?.sharepointConnected) {
                setIsSharepointAuth(true);
                ToastSwal.fire({
                    text: 'SharePoint conectado exitosamente',
                    icon: 'success',
                    timer: 5000,
                    position: 'center-end',
                });
                setSharepointModalOpen(true);
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []);

    useEffect(() => {
        if (open) {
            loadCurrentLevel();
        }
    }, [navStack, open]);

    async function loadCurrentLevel() {
        setLoading(true);
        const current = navStack[navStack.length - 1];

        try {
            switch (current.type) {
                case NAV_TYPES.ROOT:
                    await loadRootLevel();
                    break;
                case NAV_TYPES.MY_DRIVE:
                    await loadMyDrive(current);
                    break;
                case NAV_TYPES.SITE:
                    await loadSite(current);
                    break;
                case NAV_TYPES.DRIVE:
                    await loadDrive(current);
                    break;
                case NAV_TYPES.FOLDER:
                    await loadFolder(current);
                    break;
            }
        } catch {
            setMyDriveItems([]);
            setSitesItems([]);
            setCurrentItems([]);
        }

        setLoading(false);
    }

    async function loadRootLevel() {
        const myDrive = await asyncSharepointGetMyDriveRoot();
        const sites = await asyncSharepointGetSites();

        setMyDriveItems(
            myDrive.map((item: any) => ({
                id: item.id,
                name: item.name,
                folder: item.folder || undefined,
                file: item.file || undefined,
                isMyDrive: true,
            }))
        );

        setSitesItems(
            sites.map((site: any) => ({
                id: site.id,
                name: site.displayName,
                folder: {},
                isSite: true,
            }))
        );

        setCurrentItems([]);
    }

    async function loadMyDrive(current: NavEntry) {
        const items = current.id
            ? await asyncSharepointGetDriveItemChildren(current.id)
            : await asyncSharepointGetMyDriveRoot();
        setCurrentItems(items);
    }

    async function loadSite(current: NavEntry) {
        if (!current.id) return setCurrentItems([]);
        const drives = await asyncSharepointGetDrives(current.id);
        setCurrentItems(
            drives.map((drive: any) => ({
                id: drive.id,
                name: drive.name,
                folder: {},
                siteId: current.id,
                isDrive: true,
            }))
        );
    }

    async function loadDrive(current: NavEntry) {
        if (!current.siteId || !current.id) return setCurrentItems([]);
        const filesAndFolders = await asyncSharepointGetFiles(
            current.siteId,
            current.id
        );
        setCurrentItems(filesAndFolders);
    }

    async function loadFolder(current: NavEntry) {
        if (!current.id) return setCurrentItems([]);
        // OneDrive / My files: don't have siteId; use items API by id
        if (!current.siteId) {
            const children = await asyncSharepointGetDriveItemChildren(
                current.id
            );
            setCurrentItems(children);
            return;
        }
        // SharePoint: needs siteId, driveId and folder id
        if (!current.driveId) return setCurrentItems([]);
        const children = await asyncSharepointGetFiles(
            current.siteId,
            current.driveId,
            current.id
        );
        setCurrentItems(children);
    }

    function navigateInto(item: SharepointItem) {
        const current = navStack[navStack.length - 1];
        if (item.folder || item.isSite || item.isDrive) {
            let type: NavEntry['type'] = NAV_TYPES.FOLDER;
            if (item.isSite) type = NAV_TYPES.SITE;
            else if (item.isDrive) type = NAV_TYPES.DRIVE;
            else if (item.isMyDrive) type = NAV_TYPES.MY_DRIVE;

            setNavStack((stack) => [
                ...stack,
                {
                    id: item.id,
                    name: item.name,
                    type,
                    siteId: type === NAV_TYPES.SITE ? item.id : current.siteId,
                    driveId:
                        type === NAV_TYPES.DRIVE || type === NAV_TYPES.FOLDER
                            ? item.id
                            : current.driveId,
                },
            ]);
        }
    }

    function navigateBack(index: number) {
        setNavStack((stack) => stack.slice(0, index + 1));
    }

    function toggleSelect(item: SharepointItem) {
        const exists = selectedItems.find((f) => f.id === item.id);
        if (exists) {
            onFilesSelected(selectedItems.filter((f) => f.id !== item.id));
        } else {
            onFilesSelected([...selectedItems, item]);
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            textBtn="cancelar"
            textSecondBtn={`Seleccionar (${selectedItems.length})`}
            onClickSecondBtn={onClose}
            disabledSecondBtn={selectedItems.length === 0}
            maxWidth="md"
            title={'Seleccionar archivos y carpetas'}
        >
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {navStack.map((nav, idx) => (
                    <Typography
                        key={idx}
                        color={
                            idx === navStack.length - 1
                                ? 'text.primary'
                                : 'primary.main'
                        }
                        sx={{
                            cursor:
                                idx !== navStack.length - 1
                                    ? 'pointer'
                                    : 'default',
                        }}
                        onClick={() =>
                            idx !== navStack.length - 1 && navigateBack(idx)
                        }
                    >
                        {nav.name}
                    </Typography>
                ))}
            </Breadcrumbs>

            {/* Loader */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Root */}
            {!loading && atRoot && (
                <>
                    <Tabs
                        value={tabIndex}
                        onChange={(_, v) => setTabIndex(v)}
                        centered
                    >
                        <Tab label="Mis archivos" />
                        <Tab label="SharePoint" />
                    </Tabs>

                    <Box sx={{ mt: 2 }}>
                        {tabIndex === 0 && (
                            <FileList
                                items={myDriveItems}
                                selectedItems={selectedItems}
                                onItemClick={navigateInto}
                                onToggleSelect={toggleSelect}
                            />
                        )}
                        {tabIndex === 1 && (
                            <FileList
                                items={sitesItems}
                                selectedItems={selectedItems}
                                onItemClick={navigateInto}
                                onToggleSelect={toggleSelect}
                            />
                        )}
                    </Box>
                </>
            )}

            {/* Folders & Drives */}
            {!loading && !atRoot && (
                <FileList
                    items={currentItems}
                    selectedItems={selectedItems}
                    onItemClick={(item) =>
                        item.folder || item.isDrive
                            ? navigateInto(item)
                            : toggleSelect(item)
                    }
                    onToggleSelect={toggleSelect}
                />
            )}

            {/* Action bar */}
            <Box
                sx={{ display: 'flex', p: 2, gap: 2, justifyContent: 'center' }}
            >
                <ButtonGeneral
                    variant="outlined"
                    title="Revocar acceso"
                    onClick={onRevokeAccess}
                />
                <ButtonGeneral
                    variant="outlined"
                    title="Limpiar selección"
                    onClick={onClearSelection}
                />
            </Box>
        </Dialog>
    );
};
